/**
 * usePendingRoomCount — how many uploaded visuals have no room yet.
 *
 * `useStagingRoomSync` waits for quiescence before it touches the room set
 * (`isAnyRunning` — R3), so while a photo is uploading NO room is added for it.
 * With an empty list that is invisible anyway (the module shows its own waiting
 * state), but once the client already has a room the list simply does not move,
 * and a second photo looks like it was ignored until the upload settles.
 *
 * Counted from the FileAPI scope rather than from the feed, because an entry
 * exists there the moment the file is dropped — which is exactly the window the
 * room set cannot describe. An entry mid-upload has no `gcFilename` yet, so it
 * can never match a room and is correctly counted as pending.
 *
 * A file on its way OUT is not pending. Deleting removes the room first and the
 * scope entry a moment later, so for that moment the entry has no room and the
 * naive count promised a preview for a picture the client had just thrown away.
 * A failed or cancelled upload is excluded for the same reason: nothing is
 * coming, so nothing should say it is.
 */

import { useMemo } from 'react'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { claimedFilenames } from '../../store/slices/cartAttachmentsSlice'
import { FileAction, FileState } from 'components/common/FileAPI'

import { forProductCode, usePfV2VisualUpload } from '../../fileHub'
import { useStagingConfig } from './useStagingConfig'

export function usePendingRoomCount(cartItemId: CartItemId): number {
  const { stagingEntries } = usePfV2VisualUpload()
  const { rooms } = useStagingConfig(cartItemId)

  const scopeKey = usePfStore(
    (state) => state.items.find((item) => item.id === cartItemId)?.shellProductId ?? null
  )
  const attachments = usePfStore((state) => state.attachments)

  return useMemo(() => {
    // Same narrowing the reconcile uses: this product's visuals, minus the ones
    // another cart line owns.
    const claimed = claimedFilenames(attachments, cartItemId)

    return forProductCode(stagingEntries, scopeKey)
      .filter((entry) => entry.action !== FileAction.DELETE)
      .filter((entry) => entry.state !== FileState.ERROR && entry.state !== FileState.CANCELLED)
      .filter((entry) => !claimed.has(entry.gcFilename ?? ''))
      .filter((entry) => !rooms[entry.gcFilename ?? entry.id])
      .length
  }, [stagingEntries, scopeKey, attachments, cartItemId, rooms])
}
